// src/features/clients/pages/UnsatisfiedClientsPage.tsx

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../../../context/AuthContext';
import Layout from '../../../components/layout/Layout';
import {
  ThumbsDown, Settings, Loader2,
  AlertCircle,
  Frown, Meh, Star, DollarSign
} from 'lucide-react';
import BrandLoader from '../../../components/common/BrandLoader';
import CustomWhatsAppMessage from '../../../components/common/CustomWhatsAppMessage';
import ClientContactInfo from '../../../components/common/ClientContactInfo';
import PaginationControls from '../../../components/common/PaginationControls';
import { useUnsatisfiedClients, type UnsatisfiedVisit } from '../../../hooks/useUnsatisfiedClients';
import { useWahaSending } from '../../../hooks/useWahaSending';
import { formatDateWithoutTZ } from '../../../utils/dates';
import { isPro as checkIsPro } from '../../../utils/planPermissions';
import { ProLocked } from '../../../components/pro/ProLocked';

// --- Interfaces y Constantes ---

interface DailyUnsatisfiedVisits {
  dateKey: string;
  displayDate: string;
  visits: UnsatisfiedVisit[];
  count: number;
  feedbackCounts: {
    malo: number;
    neutral: number;
  };
}

const DAYS_TO_FETCH = 30;
const ITEMS_PER_PAGE = 20;
// Anotado para que no se ensanche a string[]: `useUnsatisfiedClients` pide la unión exacta.
const FEEDBACK_TYPES_TO_FETCH: Array<'neutral' | 'malo'> = ['neutral', 'malo'];
const DEFAULT_RECOVERY_MESSAGE = 'Hola @nombre, vimos tu reciente visita a @marca y nos gustaría saber más sobre tu experiencia para poder mejorar. Agradecemos mucho tu opinión. 🙏';

// --- Helpers (fuera del componente) ---

const getFeedbackIcon = (feedback: 'neutral' | 'malo') => {
    return feedback === 'malo' 
        ? <Frown className="h-4 w-4 text-red-500" /> 
        : <Meh className="h-4 w-4 text-yellow-500" />;
};

const getFeedbackColor = (feedback: 'neutral' | 'malo') => {
    return feedback === 'malo' ? 'text-red-600 dark:text-red-400' : 'text-yellow-600 dark:text-yellow-400';
};

const getFeedbackBgColor = (feedback: 'neutral' | 'malo') => {
    return feedback === 'malo' ? 'bg-red-50 dark:bg-red-500/10 border-red-200' : 'bg-yellow-50 dark:bg-yellow-500/10 border-yellow-200';
};

// --- Componente Principal ---

const UnsatisfiedClientsPage: React.FC = () => {
    const { currentUser } = useAuth();
    const contentTopRef = useRef<HTMLDivElement>(null);

    // ✅ Detección de modo de lealtad
    const loyaltyMode = currentUser?.loyaltyMode || 'visits';

    const {
        unsatisfiedVisits,
        loading,
        error,
        paginationProps,
        currentPage
    } = useUnsatisfiedClients({
        itemsPerPage: ITEMS_PER_PAGE,
        daysPeriod: DAYS_TO_FETCH,
        feedbackTypes: FEEDBACK_TYPES_TO_FETCH
    });

    // ✅ Hook de envío WAHA reutilizable
    const waha = useWahaSending({
        campaignPrefix: 'feedback_recovery',
        idMode: 'per-record',
        storageKey: 'feedbackRecoveryMessageTemplate',
        defaultMessage: DEFAULT_RECOVERY_MESSAGE,
        buttonLabels: {
            action: 'Contactar',
            actionLong: 'Contactar por WhatsApp',
            sent: 'Contactado',
        },
    });

    const [isEditingMessage, setIsEditingMessage] = useState(false);
    const [pageChanging] = useState(false);

    // ✅ Usar helper centralizado — premium también pasa
    const hasProAccess = checkIsPro(currentUser?.subscription);
    const showPaywall = !hasProAccess;

    // ✅ Cargar estados de envío cuando cambian las visitas
    // Sin restricción de Premium para que badges se vean siempre
    useEffect(() => {
        if (unsatisfiedVisits.length > 0) {
            const records = unsatisfiedVisits.map(v => ({
                id: v.id,
                clientId: v.clientId,
            }));
            waha.loadStatuses(records);
        }
    }, [unsatisfiedVisits]);

    useEffect(() => {
        if (contentTopRef.current) {
            contentTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [currentPage]);

    const groupedVisits = useMemo<DailyUnsatisfiedVisits[]>(() => {
        if (!unsatisfiedVisits.length) return [];
        const groups: Record<string, { visits: UnsatisfiedVisit[], representative: UnsatisfiedVisit }> = {};
        
        unsatisfiedVisits.forEach(visit => {
            const dateKey = new Date(visit.checkInDate).toISOString().split('T')[0];
            if (!groups[dateKey]) {
                groups[dateKey] = { visits: [], representative: visit };
            }
            groups[dateKey].visits.push(visit);
        });

        return Object.entries(groups)
          .map(([dateKey, groupData]) => {
            const feedbackCounts = groupData.visits.reduce((counts, visit) => {
                const type = visit.feedback === 'malo' ? 'malo' : 'neutral';
                counts[type]++;
                return counts;
            }, { malo: 0, neutral: 0 });
            return {
                dateKey: dateKey,
                displayDate: formatDateWithoutTZ(new Date(groupData.representative.checkInDate), { 
                    day: 'numeric', month: 'long', year: 'numeric' 
                }),
                visits: groupData.visits,
                count: groupData.visits.length,
                feedbackCounts
            };
          })
          .sort((a, b) => b.dateKey.localeCompare(a.dateKey));
    }, [unsatisfiedVisits]);




    // ✅ Helper: construir SendTarget desde UnsatisfiedVisit
    const buildSendTarget = (visit: UnsatisfiedVisit) => ({
        id: visit.id,
        clientId: visit.clientId,
        phoneNumber: visit.clientPhone,
        countryCode: visit.countryCode,
        variables: {
            nombre: visit.clientName,
            marca: currentUser?.businessName,
            puntos: visit.earnedPoints,
            ultimavisita: new Date(visit.checkInDate),
        },
        branchId: (visit as any).branchId, // ✅ v3.0.0: Multi-sesión WhatsApp (branchId de la visita)
    });

    if (showPaywall) {
        return (
            <Layout>
                <div className="space-y-6 px-2 sm:px-0">
                    <div className="animate-fade-in-up" style={{animationDelay: '0.1s'}}>
                        <h1 className="text-xl sm:text-2xl font-bold text-content flex items-center">
                            <ThumbsDown className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-orange-600 dark:text-orange-400" />
                            Seguimiento de Feedback
                        </h1>
                        <p className="mt-1 text-sm text-content-2">
                            Gestiona y haz seguimiento a clientes con feedback neutral o negativo
                        </p>
                    </div>
                    <ProLocked feature="unsatisfied-clients" />
                </div>
            </Layout>
        );
    }
    
    return (
        <Layout>
            <div className="space-y-6 px-2 sm:px-0">
                <div ref={contentTopRef} id="content-top"></div>
                
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 animate-fade-in-up">
                    <div>
                        <h1 className="text-xl sm:text-2xl font-bold text-content flex items-center">
                            <ThumbsDown className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-orange-600 dark:text-orange-400" />
                            Seguimiento de Feedback
                        </h1>
                        <p className="mt-1 text-sm text-content-2">
                            Gestiona clientes con feedback neutral o negativo de los últimos {DAYS_TO_FETCH} días
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {/* ✅ Toggle WAHA auto/manual (solo visible para Premium) */}
                        {waha.renderModeToggle()}
                        <button
                            onClick={() => setIsEditingMessage(true)}
                            className="inline-flex items-center px-3 py-2 border border-line shadow-sm text-sm rounded-md text-content-2 bg-surface hover:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
                        >
                            <Settings className="h-4 w-4 mr-2 text-blue-500" />
                            Personalizar Mensaje
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="bg-red-50 dark:bg-red-500/10 border-l-4 border-red-400 p-4 rounded animate-fade-in-up">
                        <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                    </div>
                )}

                {isEditingMessage && (
                    <div className="bg-surface shadow-md rounded-lg p-4 border-l-4 border-blue-500 animate-fade-in-up">
                        <CustomWhatsAppMessage
                            countryCode="+1"
                            phoneNumber="1234567890"
                            variables={{
                                nombre: "Cliente Ejemplo",
                                marca: currentUser?.businessName,
                                puntos: 0,
                                ultimavisita: new Date()
                            }}
                            isGlobalEditor={true}
                            onCloseEditor={() => setIsEditingMessage(false)}
                            storageKey="feedbackRecoveryMessageTemplate"
                            defaultMessage={DEFAULT_RECOVERY_MESSAGE}
                            editorTitle="Personalizar Mensaje de Recuperación"
                        />
                    </div>
                )}

                <div className="bg-surface shadow rounded-lg overflow-hidden animate-fade-in-up">
                    <div className="px-4 py-5 sm:px-6 bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-500/10 dark:to-red-500/10">
                        <h3 className="text-lg font-medium text-content flex items-center">
                            <AlertCircle className="h-5 w-5 mr-2 text-orange-500" />
                            Clientes con Feedback Negativo/Neutral ({paginationProps.totalItems} visitas)
                        </h3>
                    </div>
                    <div className="px-4 py-5 sm:p-6">
                        {loading && !pageChanging ? (
                            <div className="flex justify-center items-center py-12">
                                <BrandLoader size={36} />
                                <span className="ml-2">Cargando...</span>
                            </div>
                        ) : unsatisfiedVisits.length === 0 ? (
                            <div className="text-center py-8 animate-fade-in-up">
                                <ThumbsDown className="h-12 w-12 text-content-3 mx-auto mb-3" />
                                <p className="text-content-3">No hay clientes con feedback negativo o neutral en los últimos {DAYS_TO_FETCH} días.</p>
                                <p className="text-sm text-content-3 mt-1">¡Excelente trabajo!</p>
                            </div>
                        ) : (
                            <>
                                {/* Vista Móvil */}
                                <div className="md:hidden space-y-6">
                                    {groupedVisits.map((group) => (
                                        <div key={group.dateKey} className="animate-fade-in-up">
                                            <div className="flex items-center justify-between mb-3">
                                                <h4 className="text-base font-medium text-content">{group.displayDate}</h4>
                                                <div className="flex items-center gap-2">
                                                    {group.feedbackCounts.malo > 0 && (<div className="px-2 py-1 bg-red-100 dark:bg-red-500/20 text-red-800 dark:text-red-300 text-xs font-medium rounded-full flex items-center"><Frown className="h-3 w-3 mr-1" />{group.feedbackCounts.malo}</div>)}
                                                    {group.feedbackCounts.neutral > 0 && (<div className="px-2 py-1 bg-yellow-100 dark:bg-yellow-500/20 text-yellow-800 dark:text-yellow-300 text-xs font-medium rounded-full flex items-center"><Meh className="h-3 w-3 mr-1" />{group.feedbackCounts.neutral}</div>)}
                                                </div>
                                            </div>
                                            <div className="space-y-3">
                                                {group.visits.map((visit) => (
                                                    <div key={visit.id} className={`border rounded-lg p-3 hover:bg-surface-2 ${getFeedbackBgColor(visit.feedback)}`}>
                                                        <div className="flex justify-between items-start gap-3">
                                                            <div className="flex-1">
                                                                <ClientContactInfo clientId={visit.clientId} clientName={visit.clientName} phoneNumber={visit.clientPhone} countryCode={visit.countryCode} iconSize="sm" />
                                                                <div className="mt-2 flex items-center gap-2 text-xs text-content-3">
                                                                    {getFeedbackIcon(visit.feedback)}
                                                                    <span className={getFeedbackColor(visit.feedback)}>Feedback: {visit.feedback}</span>
                                                                    {visit.earnedPoints > 0 && (
                                                                        <>
                                                                            <span>•</span>
                                                                            {loyaltyMode === 'cashback' ? (
                                                                                <span className="flex items-center text-green-700 dark:text-green-300">
                                                                                    <DollarSign className="h-3 w-3 text-green-600 dark:text-green-400 mr-0.5" />
                                                                                    {visit.earnedPoints.toFixed(2)}
                                                                                </span>
                                                                            ) : (
                                                                                <span className="flex items-center">
                                                                                    <Star className="h-3 w-3 text-amber-500 mr-0.5" />
                                                                                    {Math.floor(visit.earnedPoints)} pts
                                                                                </span>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {/* ✅ Botón de envío del hook */}
                                                        <div className="mt-3 pt-3 border-t border-line flex justify-end">
                                                            {waha.renderSendButton(buildSendTarget(visit), 'sm')}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Vista de Escritorio */}
                                <div className="hidden md:block space-y-8">
                                    {groupedVisits.map((group) => (
                                        <div key={group.dateKey}>
                                            <div className="flex items-center justify-between mb-3">
                                                <h3 className="text-lg font-medium text-content">{group.displayDate}</h3>
                                                <div className="flex items-center gap-3">
                                                    {group.feedbackCounts.malo > 0 && (<div className="px-3 py-1 bg-red-100 dark:bg-red-500/20 text-red-800 dark:text-red-300 text-sm font-medium rounded-full flex items-center"><Frown className="h-4 w-4 mr-2" />{group.feedbackCounts.malo} Malo</div>)}
                                                    {group.feedbackCounts.neutral > 0 && (<div className="px-3 py-1 bg-yellow-100 dark:bg-yellow-500/20 text-yellow-800 dark:text-yellow-300 text-sm font-medium rounded-full flex items-center"><Meh className="h-4 w-4 mr-2" />{group.feedbackCounts.neutral} Neutral</div>)}
                                                </div>
                                            </div>
                                            <div className="overflow-x-auto border rounded-lg">
                                                <table className="min-w-full divide-y divide-line">
                                                    <thead className="bg-surface-2">
                                                        <tr>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">Cliente</th>
                                                            <th className="px-6 py-3 text-center text-xs font-medium text-content-3 uppercase tracking-wider">Feedback</th>
                                                            <th className="px-6 py-3 text-center text-xs font-medium text-content-3 uppercase tracking-wider">
                                                                {loyaltyMode === 'cashback' ? 'Cashback Generado' : 'Puntos Generados'}
                                                            </th>
                                                            <th className="px-6 py-3 text-center text-xs font-medium text-content-3 uppercase tracking-wider">Acción</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="bg-surface divide-y divide-line">
                                                        {group.visits.map((visit) => (
                                                            <tr key={visit.id} className="hover:bg-surface-2">
                                                                <td className="px-6 py-4 whitespace-nowrap"><ClientContactInfo clientId={visit.clientId} clientName={visit.clientName} phoneNumber={visit.clientPhone} countryCode={visit.countryCode} nameAsLink={true} /></td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-center"><div className={`inline-flex items-center justify-center px-2 py-1 rounded-full text-xs font-medium ${visit.feedback === 'malo' ? 'bg-red-100 dark:bg-red-500/20 text-red-800 dark:text-red-300' : 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-800 dark:text-yellow-300'}`}>{getFeedbackIcon(visit.feedback)}<span className="ml-1">{visit.feedback}</span></div></td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                                                                    <div className="inline-flex items-center justify-center text-content-2">
                                                                        {loyaltyMode === 'cashback' ? (
                                                                            <span className="flex items-center text-green-700 dark:text-green-300 font-medium">
                                                                                <DollarSign className="h-4 w-4 mr-1 text-green-600 dark:text-green-400" />
                                                                                {visit.earnedPoints.toFixed(2)}
                                                                            </span>
                                                                        ) : (
                                                                            <span className="flex items-center">
                                                                                <Star className="h-4 w-4 mr-1.5 text-amber-500" />
                                                                                {Math.floor(visit.earnedPoints)} pts
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                {/* ✅ Botón de envío del hook */}
                                                                <td className="px-6 py-4 whitespace-nowrap text-center">
                                                                    <div className="flex justify-center">
                                                                        {waha.renderSendButton(buildSendTarget(visit), 'sm')}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                
                                {paginationProps && paginationProps.totalItems > ITEMS_PER_PAGE && (
                                    <PaginationControls {...paginationProps} isLoading={loading || pageChanging} canGoNext={paginationProps.hasMore && !pageChanging} canGoPrev={paginationProps.canGoPrev && !pageChanging} />
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
            
            {pageChanging && (
                <div className="fixed bottom-4 right-4 bg-surface rounded-full shadow-lg p-3 flex items-center animate-fade-in z-50">
                    <Loader2 className="h-5 w-5 text-orange-500 animate-spin mr-2" />
                    <span className="text-sm font-medium text-content-2">Cambiando página...</span>
                </div>
            )}
        </Layout>
    );
};

export default UnsatisfiedClientsPage;